import Quill from 'quill';

const BlockEmbed: any = Quill.import('blots/block/embed');

/** Una foto della gallery, come vive nel markup dell'articolo. */
export interface FotoGalleria {
  url: string;
  didascalia: string;
  larghezza: number;
  altezza: number;
}

/** Le url arrivano dal nostro backend, ma il markup si costruisce protetto. */
function protetto(valore: string): string {
  return String(valore ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Markup di una fotogallery dentro l'articolo.
 *
 * Deliberatamente neutro: senza slider e senza JavaScript resta una sequenza
 * di foto con didascalia, leggibile e valida. Il sito pubblico la trasforma in
 * slider sulla pagina normale e in `amp-carousel` su quella AMP — lo stesso
 * schema dei video caricati dalla redazione.
 *
 * `width` e `height` sono le dimensioni vere, misurate dal browser al momento
 * della scelta: AMP le pretende per disegnare responsive, e servono a non far
 * saltare il layout mentre le immagini arrivano.
 *
 * Il contenitore e' un `div` e non un `figure`: `figcaption` e' valido solo
 * dentro un `figure`, e ogni foto ha il suo.
 */
export function markupGalleria(foto: FotoGalleria[]): string {
  const figure = foto
    .filter((f) => f.url)
    .map((f) => {
      const didascalia = protetto(f.didascalia).trim();
      return (
        `<figure class="galleria__foto">` +
        `<img src="${protetto(f.url)}" alt="${didascalia}" ` +
        `width="${Math.round(f.larghezza) || 1600}" height="${Math.round(f.altezza) || 900}" loading="lazy">` +
        (didascalia ? `<figcaption>${didascalia}</figcaption>` : '') +
        `</figure>`
      );
    })
    .join('');

  return `<div class="galleria" data-galleria>${figure}</div>`;
}

/** Rilegge le foto dal markup, per riaprire la gallery e modificarla. */
export function fotoDalMarkup(nodo: Element | null): FotoGalleria[] {
  if (!nodo) return [];

  return Array.from(nodo.querySelectorAll('figure.galleria__foto')).map((figura) => {
    const img = figura.querySelector('img');
    const didascalia = figura.querySelector('figcaption');
    return {
      url: img?.getAttribute('src') ?? '',
      // La didascalia sta nel figcaption; l'alt e' la stessa cosa scritta due
      // volte, e se la figcaption manca si ripesca da li'.
      didascalia: (didascalia?.textContent ?? img?.getAttribute('alt') ?? '').trim(),
      larghezza: Number(img?.getAttribute('width')) || 0,
      altezza: Number(img?.getAttribute('height')) || 0,
    };
  });
}

export class EmbedGalleria extends BlockEmbed {
  static blotName = 'galleria';
  static tagName = 'DIV';
  static className = 'galleria';

  static create(value: FotoGalleria[]) {
    const node = super.create();
    node.setAttribute('data-galleria', '');
    // Il markup completo comprende il contenitore: qui serve solo il contenuto.
    node.innerHTML = markupGalleria(value).replace(/^<div[^>]*>|<\/div>$/g, '');
    return node;
  }

  static value(domNode: Element): FotoGalleria[] {
    return fotoDalMarkup(domNode);
  }
}

Quill.register(EmbedGalleria);
